import { ColumnType } from '../types';
import { TableViewState } from './types';
import {
  effectiveDefaultPinned, effectivePinnable, isGroup, isSystem, stateKeyOf,
} from './columns';

/**
 * Whether a leaf ends up pinned. An effectively non-`pinnable` column ignores
 * user state and falls back to its effective `defaultPinned` — this keeps the
 * locked archetype pinned and a plain non-pinnable column unpinned. Otherwise
 * the pin comes from `state.pinned`, or from `defaultPinned` when pins are
 * untouched. Meta cascades from `parent` — group-level `pinnable`/
 * `defaultPinned` provide the default for children. Visibility is gated by the
 * caller (the tree is already visibility-filtered).
 */
export function isLeafPinned<R>(
  leaf: ColumnType<R>,
  state: TableViewState | null | undefined,
  parent?: ColumnType<R>,
): boolean {
  if (isSystem(leaf)) return false;
  if (!effectivePinnable(leaf, parent)) return effectiveDefaultPinned(leaf, parent);
  if (state && Array.isArray(state.pinned)) {
    return state.pinned.includes(stateKeyOf(leaf));
  }
  return effectiveDefaultPinned(leaf, parent);
}

/**
 * Render model C: set `fixed: 'left'` on pinned leaves in place. Within each
 * group the pinned leaves are hoisted to a leading prefix (preserving relative
 * order), which makes antd's boundary-rule trap — a group header with an
 * interior scrolling gap — impossible to emit. Standalone pinned leaves are
 * fixed where they are; antd's offset packing docks them as islands on scroll.
 *
 * This transform is the strategy point for the future group-split models
 * (B1/B2), which would relocate pinned units over the same resolved state.
 */
export function applyPinning<R>(
  columns: ColumnType<R>[],
  state: TableViewState | null | undefined,
): ColumnType<R>[] {
  return columns.map((col) => {
    if (isSystem(col)) return col;
    if (isGroup(col)) return pinGroup(col, state);
    return isLeafPinned(col, state) ? fixLeft(col) : col;
  });
}

function pinGroup<R>(
  group: ColumnType<R>,
  state: TableViewState | null | undefined,
): ColumnType<R> {
  const children = group.children ?? [];
  const pinned: ColumnType<R>[] = [];
  const rest: ColumnType<R>[] = [];
  for (const child of children) {
    if (isLeafPinned(child, state, group)) pinned.push(fixLeft(child));
    else rest.push(child);
  }
  if (pinned.length === 0) return group;
  return { ...group, children: [...pinned, ...rest] };
}

function fixLeft<R>(col: ColumnType<R>): ColumnType<R> {
  return col.fixed === 'left' ? col : { ...col, fixed: 'left' };
}
